// src/utils/colorDictionary.ts
export enum ColorName {
  Beige = 'Beige',
  White = 'White',
  LightGrey = 'Light Grey',
  Grey = 'Grey',
  DarkGrey = 'Dark Grey',
  GreyCubanit = 'Grey Cubanit',
  GreyStone = 'Grey Stone',
  GreenEucalypt = 'Green Eucalypt',
  RoseAntique = 'Rose Antique',
  BeigeAlmond = 'Beige Almond',
  GreenSalvia = 'Green Salvia',
  BeigeCashmere = 'Beige Cashmere',
  BeigeSand = 'Beige Sand',
  NaturalAccacia = 'Natural Acacia',
  NaturalWalnut = 'Natural Walnut',
  BrownOak = 'Brown Belmont Oak',
  NaturalKendalOak = 'Natural Kendal Oak',
  PebbleGrey = 'Pebble Grey',
  OliveGreen = 'Olive Green',
  AgaveGreen = 'Agave Green',
  StoneGreen = 'Stone Green',
  EstateGreen = 'Estate Green',
  DustGrey = 'Dust Grey',
  HavannaGrey = 'Havanna Grey',
  OnyxGrey = 'Onyx Grey',
  CarbonGrey = 'Carbon Grey',

  // New Collection
  GreenFjord = 'Green Fjord',
  DenimBlue = 'Denim Blue',
  GarnetRed = 'Garnet Red',
  BlackBrown = 'Black Brown',
  CaramelNude = 'Caramel Nude',
  MonumentGrey = 'Monument Grey',

  // Wood textures — plywood
  BirchPlywood = 'Birch Plywood',
  LightOak = 'Light Oak',
  NaturalOak = 'Natural Oak',
  DarkOak = 'Dark Oak',
  BlackOak = 'Black Oak',

  // Wood textures — chipboard
  NaturalCasellaOak = 'Natural Casella Oak',
  MaroneCasellaOak = 'Marone Casella Oak',
  LightCasellaOak = 'Light Natural Casella Oak ',
  BrownCasellaOak = 'Brown Casella Oak',
}

export const colorHexCodes: Record<ColorName, string> = {
  [ColorName.Beige]: '#f0e3ce',
  [ColorName.White]: '#fcfbf5',
  [ColorName.LightGrey]: '#c4c0ba',
  [ColorName.Grey]: '#9b9593',
  [ColorName.DarkGrey]: '#4b4b4b',
  [ColorName.GreyCubanit]: '#8a8076',
  [ColorName.GreyStone]: '#a69989',
  [ColorName.GreenEucalypt]: '#6e786d',
  [ColorName.RoseAntique]: '#c39d94',
  [ColorName.BeigeAlmond]: '#baa397',
  [ColorName.GreenSalvia]: '#bdbda6',
  [ColorName.BeigeCashmere]: '#d9c9be',
  [ColorName.BeigeSand]: '#e7d5c3',
  [ColorName.NaturalAccacia]: '#d4b896',
  [ColorName.NaturalWalnut]: '#8b6f47',
  [ColorName.BrownOak]: '#5a3a1a',
  [ColorName.NaturalKendalOak]: '#c4a47c',
  [ColorName.PebbleGrey]: '#b8b2aa',
  [ColorName.OliveGreen]: '#6f7358',
  [ColorName.AgaveGreen]: '#8b9a82',
  [ColorName.StoneGreen]: '#6f786c',
  [ColorName.EstateGreen]: '#4f5f4f',
  [ColorName.DustGrey]: '#7f7f7a',
  [ColorName.HavannaGrey]: '#6b6760',
  [ColorName.OnyxGrey]: '#55575a',
  [ColorName.CarbonGrey]: '#3f4142',

  // New Collection
  [ColorName.GreenFjord]: '#909e9e',
  [ColorName.DenimBlue]: '#93a7b2',
  [ColorName.GarnetRed]: '#61292e',
  [ColorName.BlackBrown]: '#422f29',
  [ColorName.CaramelNude]: '#c9845d',
  [ColorName.MonumentGrey]: '#777c84',

  // Wood textures — plywood
  [ColorName.BirchPlywood]: '#ddd0b8',
  [ColorName.LightOak]: '#b5a080',
  [ColorName.NaturalOak]: '#a08e6e',
  [ColorName.DarkOak]: '#7a6850',
  [ColorName.BlackOak]: '#2e2a26',

  // Wood textures — chipboard
  [ColorName.NaturalCasellaOak]: '#b49870',
  [ColorName.MaroneCasellaOak]: '#5a4838',
  [ColorName.LightCasellaOak]: '#d9c4a0',
  [ColorName.BrownCasellaOak]: '#d5c09c',
}

export interface PBRTextures {
  diffuse?: string // Base color/albedo map
  normal?: string // Normal map for surface detail
  roughness?: string // Roughness map
  aoRoughMetal?: string // Combined AO/Roughness/Metalness map (optional)
  displacement?: string // Height/displacement map (optional)
  envMap?: string // Environment/reflection map (optional)
  colorTint?: string // Color tint multiplied with texture (default: white)
  dimmingFactor?: number // Texture brightness dimming factor (0.0 to 1.0, default: 1.0) - 0.5 = 50% brightness
}

export interface ColorItem {
  hexCode: string
  materialCode: string
  name: string
  textureUrl?: string
  pbrTextures?: PBRTextures
  tintColor?: string
  fileURL?: string
}

export const colorDictionary: ColorItem[] = [
  {
    name: ColorName.Beige,
    hexCode: colorHexCodes[ColorName.Beige],
    materialCode: 'EGGER U115 ST9',
    fileURL: 'U115',
  },
  {
    name: ColorName.White,
    hexCode: colorHexCodes[ColorName.White],
    materialCode: 'EGGER W1000 ST9',
    fileURL: 'W1000',
  },
  {
    name: ColorName.LightGrey,
    hexCode: colorHexCodes[ColorName.LightGrey],
    materialCode: 'EGGER U763 ST9',
    fileURL: 'U763',
  },
  {
    name: ColorName.Grey,
    hexCode: colorHexCodes[ColorName.Grey],
    materialCode: 'EGGER U788 ST9',
    fileURL: 'U788',
  },
  {
    name: ColorName.DarkGrey,
    hexCode: colorHexCodes[ColorName.DarkGrey],
    materialCode: 'EGGER U963 ST9',
    fileURL: 'U963',
  },
  {
    name: ColorName.GreyCubanit,
    hexCode: colorHexCodes[ColorName.GreyCubanit],
    materialCode: 'EGGER U767 ST9',
    fileURL: 'U767',
  },
  {
    name: ColorName.GreyStone,
    hexCode: colorHexCodes[ColorName.GreyStone],
    materialCode: 'EGGER U727 ST9',
    fileURL: 'U727',
  },
  {
    name: ColorName.GreenFjord,
    hexCode: colorHexCodes[ColorName.GreenFjord],
    materialCode: 'EGGER U636 ST9',
    fileURL: 'U636',
  },
  {
    name: ColorName.DenimBlue,
    hexCode: colorHexCodes[ColorName.DenimBlue],
    materialCode: 'EGGER U540 ST9',
    fileURL: 'U540',
  },
  {
    name: ColorName.GarnetRed,
    hexCode: colorHexCodes[ColorName.GarnetRed],
    materialCode: 'EGGER U399 ST9',
    fileURL: 'U399',
  },
  {
    name: ColorName.BlackBrown,
    hexCode: colorHexCodes[ColorName.BlackBrown],
    materialCode: 'EGGER U989 ST9',
    fileURL: 'U989',
  },
  {
    name: ColorName.CaramelNude,
    hexCode: colorHexCodes[ColorName.CaramelNude],
    materialCode: 'EGGER U830 ST9',
    fileURL: 'U830',
  },
  {
    name: ColorName.MonumentGrey,
    hexCode: colorHexCodes[ColorName.MonumentGrey],
    materialCode: 'EGGER U780 ST9',
    fileURL: 'U780',
  },
  {
    name: ColorName.BeigeCashmere,
    hexCode: colorHexCodes[ColorName.BeigeCashmere],
    materialCode: 'EGGER U702 ST9',
    fileURL: 'U702',
  },
  {
    name: ColorName.BeigeSand,
    hexCode: colorHexCodes[ColorName.BeigeSand],
    materialCode: 'EGGER U156 ST9',
    fileURL: 'U156',
  },
  {
    name: ColorName.RoseAntique,
    hexCode: colorHexCodes[ColorName.RoseAntique],
    materialCode: 'EGGER U325 ST9',
    fileURL: 'U325',
  },
  {
    name: ColorName.BeigeAlmond,
    hexCode: colorHexCodes[ColorName.BeigeAlmond],
    materialCode: 'EGGER U211 ST9',
    fileURL: 'U211',
  },
  {
    name: ColorName.GreenSalvia,
    hexCode: colorHexCodes[ColorName.GreenSalvia],
    materialCode: 'EGGER U638 ST9',
    fileURL: 'U638',
  },
  {
    name: ColorName.GreenEucalypt,
    hexCode: colorHexCodes[ColorName.GreenEucalypt],
    materialCode: 'EGGER U604 ST9',
    fileURL: 'U604',
  },
  {
    name: ColorName.NaturalKendalOak,
    hexCode: colorHexCodes[ColorName.NaturalKendalOak],
    materialCode: 'EGGER H3170 ST12',
    fileURL: 'H3170',
  },
  {
    name: ColorName.PebbleGrey,
    hexCode: colorHexCodes[ColorName.PebbleGrey],
    materialCode: 'EGGER U201 ST9',
    fileURL: 'U201',
  },
  {
    name: ColorName.OliveGreen,
    hexCode: colorHexCodes[ColorName.OliveGreen],
    materialCode: 'EGGER U640 ST9',
    fileURL: 'U640',
  },
  {
    name: ColorName.AgaveGreen,
    hexCode: colorHexCodes[ColorName.AgaveGreen],
    materialCode: 'EGGER U645 ST9',
    fileURL: 'U645',
  },
  {
    name: ColorName.StoneGreen,
    hexCode: colorHexCodes[ColorName.StoneGreen],
    materialCode: 'EGGER U665 ST9',
    fileURL: 'U665',
  },
  {
    name: ColorName.EstateGreen,
    hexCode: colorHexCodes[ColorName.EstateGreen],
    materialCode: 'EGGER U669 ST9',
    fileURL: 'U669',
  },
  {
    name: ColorName.DustGrey,
    hexCode: colorHexCodes[ColorName.DustGrey],
    materialCode: 'EGGER U732 ST9',
    fileURL: 'U732',
  },
  {
    name: ColorName.HavannaGrey,
    hexCode: colorHexCodes[ColorName.HavannaGrey],
    materialCode: 'EGGER U755 ST9',
    fileURL: 'U755',
  },
  {
    name: ColorName.OnyxGrey,
    hexCode: colorHexCodes[ColorName.OnyxGrey],
    materialCode: 'EGGER U960 ST9',
    fileURL: 'U960',
  },
  {
    name: ColorName.CarbonGrey,
    hexCode: colorHexCodes[ColorName.CarbonGrey],
    materialCode: 'EGGER U968 ST9',
    fileURL: 'U968',
  },
  {
    name: ColorName.NaturalAccacia,
    hexCode: colorHexCodes[ColorName.NaturalAccacia],
    materialCode: 'EGGER H3840 ST10',
    fileURL: 'H3840',
    textureUrl: '/cdn/assets/thumbnails/H3840_minified.jpg',
    pbrTextures: {
      diffuse: '/cdn/assets/textures/wood/diffuse.jpg',
      normal: '/cdn/assets/textures/wood/normal.jpg',
      roughness: '/cdn/assets/textures/wood/rough.jpg',
      aoRoughMetal: '/cdn/assets/textures/wood/ao_rough_metal.jpg',
      displacement: '/cdn/assets/textures/wood/displacement.jpg',
      colorTint: '#d1d1d1', // White - shows natural texture color
      dimmingFactor: 1, // Reduces texture brightness to 50% (0.3 = very dim, 0.7 = slightly dim, 1.0 = full brightness)
    },
  },
  {
    name: ColorName.BrownOak,
    hexCode: colorHexCodes[ColorName.BrownOak],
    materialCode: 'EGGER H1303 ST12',
    fileURL: 'H1303',
    textureUrl: '/cdn/assets/thumbnails/H1303_minified.jpg',
    pbrTextures: {
      diffuse: '/cdn/assets/textures/wood/H1303_texturize.jpg',
      normal: '/cdn/assets/textures/wood/H1303_normal.png',
      roughness: '/cdn/assets/textures/wood/rough.jpg',
      colorTint: '#f9d9b0',
      dimmingFactor: 0.2,
    },
  },

  // ФАНЕРА
  {
    name: ColorName.BirchPlywood,
    hexCode: colorHexCodes[ColorName.BirchPlywood],
    materialCode: 'Birch Plywood',
    textureUrl: '/cdn/assets/thumbnails/placaj-mesteacan.jpg',
    tintColor: '#f7f2ea',
    fileURL: '1',
  },
  {
    name: ColorName.LightOak,
    hexCode: colorHexCodes[ColorName.LightOak],
    materialCode: 'Light Oak Plywood',
    textureUrl: '/cdn/assets/thumbnails/stejar-deschis.jpg',
    tintColor: '#cfcecd',
    fileURL: '2',
  },
  {
    name: ColorName.NaturalOak,
    hexCode: colorHexCodes[ColorName.NaturalOak],
    materialCode: 'Natural Oak Plywood',
    textureUrl: '/cdn/assets/thumbnails/stejar-natural.jpg',
    tintColor: '#a5a4a4',
    fileURL: '3',
  },
  {
    name: ColorName.DarkOak,
    hexCode: colorHexCodes[ColorName.DarkOak],
    materialCode: 'Dark Oak Plywood',
    textureUrl: '/cdn/assets/thumbnails/stejar-inchis.jpg',
    tintColor: '#807f7f',
    fileURL: '4',
  },
  {
    name: ColorName.BlackOak,
    hexCode: colorHexCodes[ColorName.BlackOak],
    materialCode: 'Black Oak Plywood',
    textureUrl: '/cdn/assets/thumbnails/stejar-negru.jpg',
    tintColor: '#5e5d5d',
    fileURL: '5',
  },

  // ДСП
  {
    name: ColorName.LightCasellaOak,
    hexCode: colorHexCodes[ColorName.LightCasellaOak],
    materialCode: 'EGGER H1367 ST17',
    textureUrl: '/cdn/assets/thumbnails/H1367-ST17.jpg',
    tintColor: '#d3cac0',
    fileURL: '2',
  },
  {
    name: ColorName.NaturalCasellaOak,
    hexCode: colorHexCodes[ColorName.NaturalCasellaOak],
    materialCode: 'EGGER H1385 ST40',
    textureUrl: '/cdn/assets/thumbnails/H1385-ST40.jpg',
    tintColor: '#b7aca1',
    fileURL: '3',
  },
  {
    name: ColorName.BrownCasellaOak,
    hexCode: colorHexCodes[ColorName.BrownCasellaOak],
    materialCode: 'EGGER H1386 ST40',
    textureUrl: '/cdn/assets/thumbnails/H1386-ST40.jpg',
    tintColor: '#8b8178',
    fileURL: '4',
  },
  {
    name: ColorName.MaroneCasellaOak,
    hexCode: colorHexCodes[ColorName.MaroneCasellaOak],
    materialCode: 'EGGER H1369 ST40',
    textureUrl: '/cdn/assets/thumbnails/H1369-ST40.jpg',
    tintColor: '#55504c',
    fileURL: '5',
  },
]

export function getColorItemByName(name: string): ColorItem | undefined {
  return colorDictionary.find(
    (c) => c.name.toLowerCase() === name.toLowerCase()
  )
}

function getColorItemByHex(hexCode: string): ColorItem | undefined {
  return colorDictionary.find(
    (c) => c.hexCode.toLowerCase() === hexCode.toLowerCase()
  )
}

export function getColorItem(nameOrHex: string): ColorItem | undefined {
  // Try to find by name first
  let colorItem = getColorItemByName(nameOrHex)

  // If not found and looks like a hex code, try hex lookup
  if (!colorItem && nameOrHex.startsWith('#')) {
    colorItem = getColorItemByHex(nameOrHex)
  }

  return colorItem
}

export const INTERIOR_COLUMN_COLOR = getColorItem(ColorName.BeigeSand)
  ?.hexCode as string
